import { httpClient } from '@shared/api';
import type {
  BillingSummary,
  IngestionStatusResponse,
  ScrapeConfirmResponse,
  ScrapeEstimateResult,
  ScrapeEstimateStart,
  ScrapeProgress,
  SourceListItem,
  SourcePagesResponse,
  UploadConfirmResponse,
  UploadEstimate,
} from '../model/types';
/**
 * Все knowledge-эндпоинты бэка richat-next.
 * Соответствует richat_backend/api/routers/knowledge.py и /knowledge_web.py.
 */
export const sourceApi = {
  /**
   * Фаза 1: POST /knowledge/upload — загрузить файл, получить оценку.
   * auto_confirm НЕ передаём — фронт всегда работает в двухфазном режиме.
   *
   * multipart/form-data: assistant_id (Form), file (File).
   * Content-Type axios выставит сам с boundary — не ставим вручную.
   */
  async upload(payload: { assistant_id: string; file: File }): Promise<UploadEstimate> {
    const form = new FormData();
    form.append('assistant_id', payload.assistant_id);
    form.append('file', payload.file);
    const response = await httpClient.post<UploadEstimate>('/knowledge/upload', form, {
      // Снимаем дефолтный application/json — axios сам поставит multipart с boundary
      headers: { 'Content-Type': undefined as unknown as string },
      // Большие файлы могут грузиться долго; до 100 МБ лимит на бэке
      timeout: 5 * 60_000,
    });
    return response.data;
  },
  /** Фаза 2: POST /knowledge/sources/{id}/confirm — запуск ингеста. */
  async confirm(
    sourceId: string,
    payload: { process_images: boolean },
  ): Promise<UploadConfirmResponse> {
    const response = await httpClient.post<UploadConfirmResponse>(
      `/knowledge/sources/${sourceId}/confirm`,
      payload,
    );
    return response.data;
  },
  /** GET /knowledge/sources?assistant_id=... */
  async list(assistantId: string): Promise<SourceListItem[]> {
    const response = await httpClient.get<SourceListItem[]>('/knowledge/sources', {
      params: { assistant_id: assistantId },
    });
    return response.data;
  },
  /** GET /knowledge/ingestion/{source_id} — для polling статуса. */
  async getIngestionStatus(sourceId: string): Promise<IngestionStatusResponse> {
    const response = await httpClient.get<IngestionStatusResponse>(
      `/knowledge/ingestion/${sourceId}`,
    );
    return response.data;
  },
  /**
   * DELETE /sources/{source_id} — удалить источник (НЕ /knowledge/sources/!).
   *
   * ВАЖНО: префикс — /sources/, не /knowledge/. Это легаси-разделение:
   * роутер /sources/ старый (остался от прежней архитектуры с OpenAI Files),
   * а /knowledge/* — новый. Все остальные операции с источниками — на
   * /knowledge/, и только удаление — на /sources/. Не унифицировать пока
   * не сделают на бэке.
   *
   * Возвращает 204. FK CASCADE автоматически чистит chunks и images в БД.
   * S3-файлы остаются как orphan'ы (известный долг бэка, в backlog).
   */
  async delete(sourceId: string): Promise<void> {
    await httpClient.delete(`/sources/${sourceId}`);
  },

  // =========================================================================
  //  Парсер сайтов (M-web1)
  // =========================================================================

  /**
   * POST /knowledge/scrape/estimate — старт сканирования сайта.
   */
  async startScrapeEstimate(payload: {
    assistant_id: string;
    url: string;
    process_images: boolean;
  }): Promise<ScrapeEstimateStart> {
    const response = await httpClient.post<ScrapeEstimateStart>(
      '/knowledge/scrape/estimate',
      payload,
    );
    return response.data;
  },

  /** GET /knowledge/scrape/estimate/{source_id} — результат scout-фазы. */
  async getScrapeEstimate(sourceId: string): Promise<ScrapeEstimateResult> {
    const response = await httpClient.get<ScrapeEstimateResult>(
      `/knowledge/scrape/estimate/${sourceId}`,
    );
    return response.data;
  },

  /** POST /knowledge/scrape/confirm — подтвердить парсинг. */
  async confirmScrape(payload: { source_id: string }): Promise<ScrapeConfirmResponse> {
    const response = await httpClient.post<ScrapeConfirmResponse>(
      '/knowledge/scrape/confirm',
      payload,
    );
    return response.data;
  },

  /**
   * GET /knowledge/scrape/{source_id}/progress — прогресс парсинга.
   * Используется ТОЛЬКО для веб-источников. Для PDF/DOCX даст 404.
   */
  async getScrapeProgress(sourceId: string): Promise<ScrapeProgress> {
    const response = await httpClient.get<ScrapeProgress>(
      `/knowledge/scrape/${sourceId}/progress`,
    );
    return response.data;
  },

  /** GET /knowledge/sources/{source_id}/billing-summary — разбивка стоимости. */
  async getBillingSummary(sourceId: string): Promise<BillingSummary> {
    const response = await httpClient.get<BillingSummary>(
      `/knowledge/sources/${sourceId}/billing-summary`,
    );
    return response.data;
  },

  /**
   * GET /knowledge/sources/{source_id}/pages — список обработанных страниц.
   *
   * Возвращает уникальные URL'ы (с количеством чанков и временем появления)
   * для веб-источника. По мере парсинга страницы появляются в выдаче —
   * сначала первые, потом всё остальные. Для PDF/DOCX вернёт пустой pages.
   *
   * Поллинг: пока источник в нетерминальном статусе (in_queue / in_progress) —
   * раз в 2-3 сек. После терминального — один раз для финальной фиксации.
   */
  async getSourcePages(sourceId: string): Promise<SourcePagesResponse> {
    const response = await httpClient.get<SourcePagesResponse>(
      `/knowledge/sources/${sourceId}/pages`,
    );
    return response.data;
  },
};
